import * as React from 'react';
import SearchInput from '../SearchInput';
import { debounce } from '../debounce';
import { ArticleSuggestion } from './types/ArticleSuggestion';
import { LoadingIndicator } from '../LoadingIndicator';
import { Article } from './Article';
import './style/articleSearch.scss';

interface ArticleSearchProps {
    articles: ArticleSuggestion[];
    initialSearchTerms: string;
    isVisible: boolean;
    searchTermCallBack: (value: string) => Promise<void>;
    goToArticle: (value: string) => void;
}

interface ArticleSearchState {
    isLoading: boolean;
    searchValue: string;
    hasSearched: boolean;
}

export class ArticleSearch extends React.Component<ArticleSearchProps, ArticleSearchState> {
    private _minSearchTermLength: number = 4;
    constructor(props: ArticleSearchProps) {
        super(props);
        this.state = {
            isLoading: false,
            searchValue: '',
            hasSearched: false
        };
        this.salesForceCallback = debounce(this.salesForceCallback.bind(this), 250);
        this.onChangeSearch = this.onChangeSearch.bind(this);
    }

    async componentDidUpdate(prevProps: ArticleSearchProps): Promise<void> {
        if (this.props.isVisible && this.props.isVisible !== prevProps.isVisible) {
            this.setState({ isLoading: true });
            await this.props.searchTermCallBack(this.props.initialSearchTerms);
            this.setState({ isLoading: false });
        }
    }

    render(): React.ReactNode {
        return (
            <div className="article-search">
                <div className="article-search__input-container">
                <SearchInput
                    onChange={this.onChangeSearch}
                    placeholderText="Search Articles"
                />
                </div>
                <div className="suggested-articles">
                    <div className="suggested-articles__header">
                        {!this.state.isLoading && this.renderHeaderText()}
                    </div>
                    {this.renderListBody()}
                </div>
            </div>
        );
    }

    renderListBody(): React.ReactNode {
        if (this.state.isLoading) {
            return (
                <div className="suggested-articles__loading">
                    <LoadingIndicator />
                    <div className="suggested-articles__loading-text">
                        Searching Articles...
                    </div>
                </div>
            );
        }
        if (!this.props.articles.length && !this.state.isLoading && this.state.hasSearched) {
            return (
                <div className="suggested-articles__no-articles">
                    Try searching again or submit a support ticket and
                     we'll have our team take a look.
                </div>
            );
        }
        return (
            <ul className="suggested-articles__articles">
                {this.props.articles.map(article => {
                    return this.renderArticleLink(article);
                })}
            </ul>
        );
    }

    renderArticleLink(article: ArticleSuggestion): React.ReactNode {
        return (
            <Article article={article} goToArticle={this.props.goToArticle} key={`article-${article.id}`} />
        );
    }

    renderHeaderText(): string {
        const { hasSearched } = this.state;
        const { articles } = this.props;
        if (!articles.length && hasSearched) {
            return 'No articles found';
        } else if (articles.length && hasSearched) {
            return 'Search Results';
        } else {
            return 'Suggested articles:';
        }
    }

    onChangeSearch(value: string): void {
        this.setState({searchValue: value});
        if (value.length < this._minSearchTermLength) {
            return;
        }
        this.salesForceCallback(value);
    }

    async salesForceCallback(value: string): Promise<void> {
        this.setState({isLoading: true});
        await this.props.searchTermCallBack(value);
        this.setState({isLoading: false, hasSearched: true});
    }
}

export default { ArticleSearch };